import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import Bluebird from 'bluebird';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Breadcrumbs,
  Card,
  CardContent,
  Grid,
  Typography,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Undo as UndoIcon,
  Redo as RedoIcon,
  Save as SaveIcon,
  RotateLeft as RotateLeftIcon,
  Home as HomeIcon,
} from '@mui/icons-material';
import { generatePath, Link } from 'react-router-dom';
import { confirmAlert } from 'react-confirm-alert';
import Description from '../Components/Description';
import Schedule from '../Components/Schedule';
import { fetchRescueSizesIfNeeded } from '../../../../actions/rescueSizes';
import {
  fetchSiteDonorsLocationsIfNeeded,
  fetchSitePickupLocationsIfNeeded,
  fetchSiteReceiversIfNeeded,
  fetchSiteRescuersIfNeeded,
  removeSiteTodo,
} from '../../../../actions/sites';
import useActiveSite from '../../../../hooks/useActiveSite';
import {
  createDraftDonation,
  diffDonation,
  DONATION_TYPE_SD,
  saveDonation,
  setDonationDiffInflight,
} from '../../../../actions/donationNew';
import DonationContext from '../../../../context/DonationContext/DonationContext';
import useDonationManageActions from '../../../../hooks/useDonationManageActions';
import Pickups from '../Components/Pickups';
import Calendar from '../Components/Calendar';
import { donationManageActions } from '../../../../helpers/donations';
import OverlayLoader from '../../../../components/OverlayLoader';
import useNotificationService from '../../../../hooks/useNotificationService';
import routes from '../../../../routes';
import usePermission from '../../../../hooks/usePermission';
import ACLService from '../../../../services/acl';
import { FREQUENCY_ONCE, FREQUENCY_WEEKLY, FREQUENCY_WEEKLY_EVERY_WEEK } from '../../../../models/donationsNew';
import { fetchSystemSettingsIfNeeded } from '../../../../actions/systemSettings';
import errorMessages from '../../../../assets/errorMessages';
import { validateDonation } from '../donationValidator';
import * as authService from '../../../../services/auth';
import { Roles } from '../../../../models/roles';
import { fetchReceiversIfNeeded } from '../../../../actions/receivers';
import {
  StyledRescuerBreadcrumb,
  StyledRescuerTextBreadcrumb,
} from '../../../rescuerDashboard/rescuerDashboardMobile/Components/StyledRescuerBreadcrumb';
import ConfirmationDialog from '../../../../components/ConfirmationDialog';

const headingSx = {
  fontSize: '15px',
  flexBasis: '33.33%',
  flexShrink: 0,
  display: 'flex',
  columnGap: 1,
  alignItems: 'baseline',
};

const buttonsRowSx = (theme) => ({
  display: 'flex',
  columnGap: '8px',
  [theme.breakpoints.only('xs')]: {
    marginTop: 1,
    flexGrow: 1,
    flexShrink: 0,
  },
  [theme.breakpoints.only('md')]: {
    marginTop: 1,
    flexGrow: 1,
    flexShrink: 0,
  },
});

const AGENDA_DESCRIPTION = 'Description';
const AGENDA_SCHEDULE = 'Schedule';

const hasDescriptionValidationErrors = errors => ['lbs'].some(field => errors.includes(field));

const hasScheduleValidationErrors = errors => errors.length > 0 && !hasDescriptionValidationErrors(errors);

const DonationCreate = ({ isMobileView, match, location, history }) => {
  const dispatch = useDispatch();
  const activeSite = useActiveSite();
  const [firstFoodDonorChoice, setFirstFoodDonorChoice] = useState(
    match.params.location_id ? parseInt(match.params.location_id, 10) : null
  );
  const [expanded, setExpanded] = React.useState(AGENDA_DESCRIPTION);
  const rescueSizesInflight = useSelector(state => state.entities.rescueSizes.inflight);
  const pickupLocations = useSelector(state => state.entities.sites.pickupLocations);
  const foodDonors = useSelector(state => state.entities.sites.donorsLocations);
  const receivers = useSelector(state => state.entities.sites.receivers);
  const foodDonor = foodDonors.byId[match.params.location_id];
  const pickupLocation = pickupLocations.byId[match.params.pickup_location_id];
  const receiver = receivers.byId[match.params.receiver_id];
  const donationData = useSelector(state => state.ui.donation_new.draft);
  const currentDonationState = useSelector(state => state.ui.donation_new.currentState);
  const futureDonationState = useSelector(state => state.ui.donation_new.futureState);
  const diffInflight = useSelector(state => state.ui.donation_new.diffInflight);
  const donationFormErrors = useSelector(state => state.ui.donation_new.errors);
  const { addErrorNotification, addSuccessNotification } = useNotificationService();
  const hasFoodDonorsList = usePermission({
    resource: ACLService.resources.foodDonorsList,
  });

  const isDonorLinkDonation =
    match.path === routes.donor_link_donation_add || match.path === routes.donor_link_with_pickup_donation_add;
  const hasFoodDonorDefined = donationData && (!!donationData.location || isDonorLinkDonation);
  const hasDefinedDonorAndLocation = match.path === routes.donation_add_new2;
  const hasDefinedDonorLocationAndReceiver = match.path === routes.donation_add_from_map;
  const isDonorLinkRegistration = location?.state?.isDonorLinkRegistration;

  const donorLinkExternalId = location?.state?.donor_link_external_id || null;

  const handleFoodDonorChange = foodDonorId => {
    if (firstFoodDonorChoice) {
      return;
    }

    setFirstFoodDonorChoice(foodDonorId);
  };

  const {
    clearDonation,
    undoLastAction,
    redoLastAction,
    resetDonation,

    setError,
    removeError,

    setFoodDonor,
    setPickupLocation,
    setDescription,
    setLBSValue,
    setAddFoodTypes,
    setRemoveFoodTypes,
    setAddFoodSubCategory,
    setRemoveFoodSubCategory,
    setFoodTypeOther,
    setRescueSize,
    setFoodSize,
    setAddDayOfWeek,
    setRemoveDayOfWeek,

    setDonorLinkDonation,

    setAddDayOfMonth,
    setDayOfMonthDay,
    setDayOfMonthOrdinal,
    setRemoveDayOfMonth,

    setStartDate,
    setEndDate,
    setPauseDate,
    setResumeDate,
    setFrequency,
    setAppendPickup,
    setRemovePickup,
    setPickupReceiver,
    setPickupAdopter,
    setPickupRescuer,
    setPickupRescuerNotes,
    setPickupBegin,
    setPickupEnd,
    setAllDaysTheSame,
    clearAllDaysTheSame,
    setAllPickupsTheSame,
    clearAllPickupsTheSame,

    setRescuePickupBegin,
    setRescuePickupEnd,
    setRescueRescuerNotes,
    setRescueCanceller,
    setRescueRescuer,
    setNationalDonation,
    setGroupIds,
  } = useDonationManageActions();

  useEffect(() => {
    dispatch(
      createDraftDonation({
        donation: {
          description: '',
          donor_link_donation: isDonorLinkDonation,
          lbs: null,
          start_date: {
            value: moment().format('YYYY-MM-DD'),
            format: 'YYYY-MM-DD',
          },
          end_date: null,
          pause_date: null,
          resume_date: null,
          frequency: isDonorLinkDonation ? FREQUENCY_WEEKLY : FREQUENCY_ONCE,
          frequency_every: isDonorLinkDonation ? FREQUENCY_WEEKLY_EVERY_WEEK : null,
          days_of_week: {
            mon: {
              checked: false,
              allow_uncheck: null,
            },
            tue: {
              checked: false,
              allow_uncheck: null,
            },
            wed: {
              checked: false,
              allow_uncheck: null,
            },
            thu: {
              checked: false,
              allow_uncheck: null,
            },
            fri: {
              checked: false,
              allow_uncheck: null,
            },
            sat: {
              checked: false,
              allow_uncheck: null,
            },
            sun: {
              checked: false,
              allow_uncheck: null,
            },
          },
          days_of_month: {
            mon: [],
            tue: [],
            wed: [],
            thu: [],
            fri: [],
            sat: [],
            sun: [],
          },
          all_days_are_the_same: true,
          all_pickups_are_the_same: true,
          food_types: ['fresh'],
          food_type_other: null,
          rescue_size: null,
          location: null,
          pickup_location: null,
        },
      })
    );
    dispatch(fetchSiteReceiversIfNeeded(activeSite.id));
    dispatch(fetchSiteDonorsLocationsIfNeeded(activeSite.id));
    dispatch(fetchSitePickupLocationsIfNeeded(activeSite.id));
    dispatch(fetchRescueSizesIfNeeded());
    dispatch(fetchSiteRescuersIfNeeded(activeSite.id));
    dispatch(fetchSystemSettingsIfNeeded()); // used in LBSSelector
    dispatch(fetchReceiversIfNeeded());

    if (location.state && location.state.copy && location.state.rescue) {
      handleFoodDonorChange(location.state.rescue.location_id);
      setFoodDonor({
        id: location.state.rescue.location_id,
        name: location.state.rescue.location,
      });
      setPickupLocation({
        name: location.state.rescue.pickup_location_name,
        id: location.state.rescue.pickup_location_id,
      })
      setRescueSize({ id: location.state.rescue.rescue_size_id });
      location.state.rescue.food_types.split(',').forEach(foodType => setAddFoodTypes(foodType));
      location.state.rescue.food_type_other && setFoodTypeOther(location.state.rescue.food_type_other);
      location.state.rescue.slug && setDescription(location.state.rescue.slug);
      setPickupRescuerNotes({
        rescuer_notes: location.state.rescue.rescuer_notes,
      });
      setPickupReceiver({ receiver_id: location.state.rescue.receiver_id });
      setPickupRescuer({ rescuer_id: location.state.rescue.claimer_id });
      setPickupBegin({ begin: moment(location.state.rescue.pickup_begin, 'hh:mm').format('hh:mm:ss') });
      setPickupEnd({ end: moment(location.state.rescue.pickup_end, 'hh:mm').format('hh:mm:ss') });

      // clear location.state
      history.replace({
        ...location,
        state: {
          ...location.state,
          copy: false,
          rescue: undefined,
        },
      });
    }

    return () => clearDonation();
  }, [dispatch]);

  useEffect(() => {
    if (!foodDonors.inflight && foodDonor) {
      setFoodDonor(foodDonor);
    }
  }, [foodDonors.inflight, Object.keys(foodDonors.byId).length]);

  useEffect(() => {
    if (!pickupLocations.inflight && pickupLocation) {
      setPickupLocation(pickupLocation);
      triggerFormChange();
    }
  }, [pickupLocations.inflight, Object.keys(pickupLocations.byId).length]);

  useEffect(() => {
    if (!receivers.inflight && receiver) {
      setPickupReceiver({ receiver_id: receiver.id });
    }
  }, [receivers.inflight, Object.keys(receivers.byId).length]);

  useEffect(() => {
    if (isDonorLinkDonation) {
      setFrequency(FREQUENCY_WEEKLY);
      setDonorLinkDonation(true);
    }
  }, [isDonorLinkDonation]);

  const onSubmit = async () => {
    try {
      const validation = validateDonation(donationData);

      if (!validation.isValid) {
        return Object.keys(validation.errors).forEach(error => setError(error, validation.errors[error]));
      }

      const donation = await dispatch(
        saveDonation(null, donationData.location.id, currentDonationState, {
          merge_donor_link_external_id: donorLinkExternalId,
        })
      );
      addSuccessNotification('Donation created!');
      if (isDonorLinkDonation) {
        const todoItem = location?.state?.todoItem;
        // Remove todo item if accessed through todo card review
        if (todoItem?.entityId && todoItem?.entityType && todoItem?.todoType) {
          dispatch(removeSiteTodo(activeSite.id, todoItem.entityId, todoItem.entityType, todoItem.todoType, true));
        }
        return history.push(
          generatePath(routes.donor_link_donation_edit, { donationId: donation.id, location_id: foodDonor.id }),
          { isDonorLinkRegistration: true }
        );
      }
      return history.push(generatePath(routes.donation_edit, { donationId: donation.id }));
    } catch (e) {
      dispatch(setDonationDiffInflight(false));

      return addErrorNotification(e, 'Unable to create donation');
    }
  };

  useEffect(() => {
    // Do not triggerFormChange here if donor, location and receiver are predefined otherwise it will overwrite them
    if (!hasDefinedDonorLocationAndReceiver) {
      // triggerFormChange after firstFoodDonorChoice is defined
      triggerFormChange();
    }
  }, [firstFoodDonorChoice]);

  useEffect(() => {
    const unblock = history.block((loc) => {
      if (loc?.state?.isDonorLinkRegistration) {
        return true;
      }

      if (!isDonorLinkRegistration) {
        return true;
      }

      confirmAlert({
        message: 'You have unsaved changes, are you sure you want to leave?',
        buttons: [
          {
            label: 'Nevermind',
            color: 'secondary',
          },
          {
            label: 'Yes',
            color: 'primary',
            onClick: () => {
              unblock();
              history.push(generatePath(loc.pathname));
            },
          },
        ],
        customUI: ({ message, onClose, buttons }) => (
          <ConfirmationDialog buttons={buttons} closeDialog={onClose} message={message} />
        ),
      });

      return false;
    });

    return () => {
      unblock();
    };
  }, [history, isDonorLinkRegistration]);

  const triggerFormChange = () => {
    if (firstFoodDonorChoice) {
      return Bluebird.try(() => dispatch(diffDonation(null, firstFoodDonorChoice))).catch(e => {
        addErrorNotification(e, e.message || 'Unknown error during modifying donation');
        dispatch(setDonationDiffInflight(false));
        undoLastAction();
      });
    }
  };

  const handleFieldChange = (actionName, fieldValue) => {
    switch (actionName) {
      case donationManageActions.set_food_donor:
        handleFoodDonorChange(fieldValue.id);
        setFoodDonor(fieldValue);
        triggerFormChange();
        break;

      case donationManageActions.set_pickup_location:
        setPickupLocation(fieldValue);
        triggerFormChange();
        break;

      case donationManageActions.set_description:
        setDescription(fieldValue);
        break;

      case donationManageActions.set_lbs_value:
        setLBSValue(fieldValue);
        if (fieldValue === '') {
          setError('lbs', errorMessages.REQUIRED.message);
        } else {
          removeError('lbs');
        }
        triggerFormChange();
        break;

      case donationManageActions.set_rescue_size:
        setRescueSize(fieldValue);
        break;

      case donationManageActions.set_food_size:
        setFoodSize(fieldValue);
        break;

      case donationManageActions.add_food_type:
        setAddFoodTypes(fieldValue);
        break;

      case donationManageActions.remove_food_type:
        setRemoveFoodTypes(fieldValue);
        break;

      case donationManageActions.add_food_sub_category:
        setAddFoodSubCategory(fieldValue);
        break;

      case donationManageActions.remove_food_sub_category:
        setRemoveFoodSubCategory(fieldValue);
        break;

      case donationManageActions.set_food_type_other:
        setFoodTypeOther(fieldValue);
        triggerFormChange();
        break;

      case donationManageActions.set_frequency:
        setFrequency(fieldValue);
        triggerFormChange();
        break;

      case donationManageActions.add_day_of_week:
        setAddDayOfWeek(fieldValue);
        triggerFormChange();
        break;

      case donationManageActions.remove_day_of_week:
        setRemoveDayOfWeek(fieldValue);
        triggerFormChange();
        break;

      case donationManageActions.add_day_of_month:
        setAddDayOfMonth(fieldValue);
        triggerFormChange();
        break;

      case donationManageActions.set_day_of_month_day:
        setDayOfMonthDay(fieldValue);
        triggerFormChange();
        break;

      case donationManageActions.set_day_of_month_ordinal:
        setDayOfMonthOrdinal(fieldValue);
        triggerFormChange();
        break;

      case donationManageActions.remove_day_of_month:
        setRemoveDayOfMonth(fieldValue);
        triggerFormChange();
        break;

      case donationManageActions.set_start_date:
        setStartDate({
          startDate: fieldValue,
        });
        triggerFormChange();
        break;

      case donationManageActions.set_end_date:
        setEndDate(fieldValue);
        triggerFormChange();
        break;

      case donationManageActions.set_pause_date:
        setPauseDate(fieldValue);
        triggerFormChange();
        break;

      case donationManageActions.set_resume_date:
        setResumeDate(fieldValue);
        triggerFormChange();
        break;

      case donationManageActions.set_all_days_are_the_same:
        setAllDaysTheSame();
        triggerFormChange();
        break;

      case donationManageActions.clear_all_days_are_the_same:
        clearAllDaysTheSame();
        triggerFormChange();

        break;
      case donationManageActions.set_all_pickups_are_the_same:
        setAllPickupsTheSame();
        triggerFormChange();
        break;

      case donationManageActions.clear_all_pickups_are_the_same:
        clearAllPickupsTheSame();
        triggerFormChange();
        break;

      case donationManageActions.add_pickup:
        setAppendPickup(fieldValue);
        triggerFormChange();
        break;

      case donationManageActions.remove_pickup:
        setRemovePickup(fieldValue);
        triggerFormChange();
        break;

      case donationManageActions.set_pickup_receiver:
        setPickupReceiver(fieldValue);
        triggerFormChange();
        break;

      case donationManageActions.set_pickup_adopter:
        setPickupAdopter(fieldValue);
        triggerFormChange();
        break;

      case donationManageActions.set_pickup_rescuer:
        setPickupRescuer(fieldValue);
        triggerFormChange();
        break;

      case donationManageActions.set_pickup_rescuer_notes:
        setPickupRescuerNotes(fieldValue);
        triggerFormChange();
        break;

      case donationManageActions.set_pickup_begin:
        setPickupBegin(fieldValue);
        triggerFormChange();
        break;

      case donationManageActions.set_pickup_end:
        setPickupEnd(fieldValue);
        triggerFormChange();
        break;

      case donationManageActions.set_rescue_pickup_begin:
        setRescuePickupBegin(fieldValue);
        triggerFormChange();
        break;

      case donationManageActions.set_rescue_pickup_end:
        setRescuePickupEnd(fieldValue);
        triggerFormChange();
        break;

      case donationManageActions.set_rescue_rescuer_notes:
        setRescueRescuerNotes(fieldValue);
        triggerFormChange();
        break;

      case donationManageActions.set_rescue_canceller:
        setRescueCanceller(fieldValue);
        triggerFormChange();
        break;

      case donationManageActions.set_rescue_rescuer:
        setRescueRescuer(fieldValue);
        triggerFormChange();
        break;

      case donationManageActions.set_national_donation:
        setNationalDonation(fieldValue);
        break;

      case donationManageActions.set_group_ids:
        setGroupIds(fieldValue);
        triggerFormChange();
        break;

      default:
        console.error(`---->>>> #Unknown donation field "${actionName}" with given value "${fieldValue}"`);
    }
  };

  const handleAccordionChange = panel => (event, isExpanded) => {
    if (!hasFoodDonorDefined) {
      return setExpanded(AGENDA_DESCRIPTION);
    }
    setExpanded(isExpanded ? panel : expanded === AGENDA_DESCRIPTION ? AGENDA_SCHEDULE : AGENDA_DESCRIPTION);
  };

  if (
    !authService.currentlyLoggedInOrImpersonatingUserHasAnyRoleInCurrentlySelectedSite([
      Roles.Admin,
      Roles.NationalSiteDirector,
      Roles.SiteDirector,
      Roles.SiteCoordinator,
    ])
  ) {
    return history.push(routes.index);
  }

  if (donationData === null || rescueSizesInflight || foodDonors.inflight || pickupLocations.inflight) {
    return 'loading...';
  }

  const renderDonationButtons = () => (
    <Box sx={{ marginTop: 2, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap' }}>
      <Box sx={buttonsRowSx}>
        <Button
          disabled={currentDonationState.length === 0}
          color="primary"
          variant="contained"
          type="button"
          onClick={onSubmit}
          startIcon={<SaveIcon />}
          data-testid="save-schedule"
        >
          Save {expanded}
        </Button>
      </Box>
      <Box sx={buttonsRowSx}>
        <Button
          color="secondary"
          variant="contained"
          type="button"
          startIcon={<RotateLeftIcon />}
          disabled={currentDonationState.length === 0}
          onClick={() => {
            resetDonation();
            triggerFormChange();
          }}
        >
          Reset
        </Button>

        <Button
          startIcon={<UndoIcon />}
          color="secondary"
          variant="contained"
          disabled={currentDonationState.length === 0}
          onClick={() => {
            undoLastAction();
            triggerFormChange();
          }}
        >
          Undo
        </Button>

        <Button
          startIcon={<RedoIcon />}
          color="secondary"
          variant="contained"
          disabled={futureDonationState.length === 0}
          onClick={() => {
            redoLastAction();
            triggerFormChange();
          }}
        >
          Redo
        </Button>
      </Box>
    </Box>
  );

  return (
    <DonationContext.Provider
      value={{
        donationType: DONATION_TYPE_SD,
        onFieldChange: handleFieldChange,
        isLoading: diffInflight,
        hasFoodDonorDefined: hasFoodDonorDefined,
        showFoodDonorSelector: !hasDefinedDonorAndLocation && !isDonorLinkDonation,
        isMobileView: isMobileView,
        isNew: true,
        errors: donationFormErrors,
      }}
    >
      {isMobileView ? (
        <Breadcrumbs aria-label="Breadcrumbs">
          <StyledRescuerBreadcrumb
            component={Link}
            to={routes.index}
            label="Home"
            icon={<HomeIcon style={{ color: '#fff' }} fontSize="small" />}
          />
          {(hasDefinedDonorAndLocation || isDonorLinkDonation) && (
            <StyledRescuerTextBreadcrumb
              component={Link}
              to={generatePath(routes.foodDonor, {
                foodDonorId: match.params.location_id,
              })}
              label="foodDonor.name"
            />
          )}
          <StyledRescuerTextBreadcrumb component="a" href="#" label="Add Donation" />
        </Breadcrumbs>
      ) : (
        <Breadcrumbs aria-label="Breadcrumbs">
          {hasFoodDonorsList ? (
            <Link color="inherit" to={routes.foodDonors}>
              Food Donors
            </Link>
          ) : (
            <Typography color="textPrimary">Food Donors</Typography>
          )}

          {(hasDefinedDonorAndLocation || isDonorLinkDonation) && (
            <Link
              color="inherit"
              to={generatePath(routes.foodDonor, {
                foodDonorId: match.params.location_id,
              })}
            >
              {foodDonor.name}
            </Link>
          )}

          <Typography color="textPrimary">Add New Donation</Typography>
        </Breadcrumbs>
      )}

      <Box sx={{ overflow: 'hidden' }}>
        <Grid container spacing={3}>
          <Grid justifyContent="flex-start" alignItems="stretch" item xs={12} md={6} lg={8}>
            <Grid item xs={12}>
              {isDonorLinkDonation && donationData?.location?.name && (
                <Typography variant="h6" sx={{ padding: 1 }}>
                  Please complete this form in order to activate the Donor Link for {donationData.location.name}
                </Typography>
              )}
              <Accordion
                elevation={25}
                expanded={expanded === AGENDA_DESCRIPTION}
                onChange={handleAccordionChange(AGENDA_DESCRIPTION)}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel-description"
                  id="description-header"
                >
                  <Typography sx={headingSx}>
                    Description
                    {hasDescriptionValidationErrors(Object.keys(donationFormErrors)) && (
                      <Typography component="span" color="error">
                        Missing info
                      </Typography>
                    )}
                  </Typography>
                </AccordionSummary>

                <AccordionDetails>
                  <Description />
                </AccordionDetails>
              </Accordion>

              <Accordion
                disabled={!hasFoodDonorDefined}
                elevation={25}
                expanded={expanded === AGENDA_SCHEDULE}
                onChange={handleAccordionChange(AGENDA_SCHEDULE)}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel-schedule" id="schedule-header">
                  <Typography sx={headingSx}>
                    Schedule
                    {hasScheduleValidationErrors(Object.keys(donationFormErrors)) && (
                      <Typography component="span" color="error">
                        Validation error
                      </Typography>
                    )}
                  </Typography>

                  {!hasFoodDonorDefined && (
                    <Typography sx={{ fontSize: '15px', color: 'text.secondary' }}>
                      Please select Food Donor first!
                    </Typography>
                  )}
                </AccordionSummary>

                <AccordionDetails>
                  <OverlayLoader isLoading={diffInflight} wrapperStyles={{ width: '100%' }}>
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <Schedule receiverPreselected={receiver} isDonorLinkDonation={isDonorLinkDonation} />
                      </Grid>

                      <Grid item xs={12}>
                        <Pickups />
                      </Grid>
                    </Grid>
                  </OverlayLoader>
                </AccordionDetails>
              </Accordion>

              {!isMobileView && renderDonationButtons()}
            </Grid>
          </Grid>

          <Grid item xs>
            <Card square elevation={25}>
              <CardContent>
                <Calendar />
              </CardContent>
            </Card>
          </Grid>

          {isMobileView && (
            <Grid item xs>
              {renderDonationButtons()}
            </Grid>
          )}
        </Grid>
      </Box>
    </DonationContext.Provider>
  );
};

export default DonationCreate;
